/**
 * A job site's address from one line (Warren, 21 October 2026: "one address field with autocomplete fills the street,
 * city, state, ZIP and coordinates"). Google Places fills the parts when the partner picks a suggestion; when it is not
 * there (no key, no network, or the partner typed the address and never picked one), this reads the typed line. The
 * Admin form and the Api run the same rules.
 *
 * The state is stored as its two-letter code (CA, ON) and the country as US or CA, so a utility rate table can be
 * matched from them. What is not recognised is kept as typed, never guessed.
 *
 * Pure, no I/O.
 */
import { homeCountryFromAny, normalizePostalCode, resolveHomeRegion } from './homeAddress.js';
const US_ZIP_END = /(?:^|[\s,])(\d{5})(?:[\s-]?(\d{4}))?$/;
const CA_POSTAL_END = /(?:^|[\s,])([A-Za-z]\d[A-Za-z])[\s-]?(\d[A-Za-z]\d)$/;
const COUNTRY_END = /[\s,]+(U\.?\s?S\.?(?:\s?A\.?)?|United States(?: of America)?|Canada|CAN)$/i;
/** Street types: in a line typed without commas, the street ends at the last of these (then any direction or unit). */
const STREET_TYPES = new Set(['ST', 'STREET', 'AVE', 'AV', 'AVENUE', 'RD', 'ROAD', 'BLVD', 'BOULEVARD', 'DR', 'DRIVE', 'WAY', 'LN', 'LANE',
    'CT', 'COURT', 'PL', 'PLACE', 'HWY', 'HIGHWAY', 'PKWY', 'PARKWAY', 'CIR', 'CIRCLE', 'TER', 'TERRACE', 'TRL', 'TRAIL', 'CRES', 'CRESCENT',
    'SQ', 'SQUARE', 'PLZ', 'PLAZA', 'ALY', 'ALLEY', 'LOOP', 'RUN', 'PIKE', 'ROW', 'GATE', 'GROVE', 'GRV', 'CRT', 'EXPY', 'FWY', 'TPKE', 'XING']);
const DIRECTIONS = new Set(['N', 'S', 'E', 'W', 'NE', 'NW', 'SE', 'SW', 'NORTH', 'SOUTH', 'EAST', 'WEST']);
const UNITS = new Set(['SUITE', 'STE', 'UNIT', 'APT', 'BLDG', 'FL', 'FLOOR', 'RM', 'ROOM']);
const tidy = (s) => s.replace(/\s+/g, ' ').trim().replace(/^[,\s]+|[,\s]+$/g, '');
const word = (w) => w.replace(/[.,]+$/, '').toUpperCase();
/** The country a postal code's shape names: five digits for the US, A1A 1A1 for Canada. */
export function countryFromPostal(zip) {
    const v = (zip ?? '').trim();
    if (normalizePostalCode('US', v))
        return 'US';
    if (normalizePostalCode('CA', v))
        return 'CA';
    return null;
}
/** Street and city from what is left of the line once the region, postal code and country are read off its end. */
function streetAndCity(rest) {
    const comma = rest.lastIndexOf(',');
    if (comma > 0)
        return { street: tidy(rest.slice(0, comma)), city: tidy(rest.slice(comma + 1)) };
    const words = rest.split(' ').filter(Boolean);
    let end = -1;
    for (let i = words.length - 2; i >= 1; i--)
        if (STREET_TYPES.has(word(words[i]))) {
            end = i;
            break;
        }
    if (end < 0)
        return { street: tidy(rest), city: '' };
    if (DIRECTIONS.has(word(words[end + 1] ?? '')) && end + 2 < words.length)
        end += 1;
    // A unit stays with the street: "Suite 5", "Unit 3", "#12" or "# 12".
    const unit = words[end + 1] ?? '';
    const take = unit.startsWith('#') ? (unit.length > 1 ? 1 : 2) : UNITS.has(word(unit)) ? 2 : 0;
    if (take && end + take + 1 < words.length)
        end += take;
    return { street: tidy(words.slice(0, end + 1).join(' ')), city: tidy(words.slice(end + 1).join(' ')) };
}
/**
 * "1450 W Shaw Ave, Fresno, CA 93711" into its parts. Reads from the end: an optional country, the postal code, then
 * the state or province (by code or name, one to four words, never across a comma), then the city after the last
 * comma, or after the street type when the line has no commas. Null when no street is left.
 */
export function parseSiteAddressLine(line) {
    let s = tidy(line ?? '');
    if (!s)
        return null;
    let country = null;
    const c = COUNTRY_END.exec(s);
    if (c) {
        // "U.S.A." and "U. S." read as USA and US; "United States" keeps its space.
        const raw = c[1];
        country = homeCountryFromAny(/^u\W*s\W*a?\W*$/i.test(raw) ? raw.replace(/[.\s]/g, '') : raw);
        s = tidy(s.slice(0, c.index));
    }
    let zip = '';
    const ca = CA_POSTAL_END.exec(s), us = ca ? null : US_ZIP_END.exec(s);
    if (ca) {
        zip = `${ca[1]} ${ca[2]}`.toUpperCase();
        country = country ?? 'CA';
        s = tidy(s.slice(0, ca.index));
    }
    else if (us) {
        zip = us[2] ? `${us[1]}-${us[2]}` : us[1];
        country = country ?? 'US';
        s = tidy(s.slice(0, us.index));
    }
    let state = '';
    const comma = s.lastIndexOf(',');
    const tail = s.slice(comma + 1).trim().split(' ').filter(Boolean);
    for (let n = Math.min(4, tail.length); n >= 1; n--) {
        // Never the whole line: something must be left for the street.
        if (comma < 0 && n === tail.length)
            continue;
        const region = resolveHomeRegion(tail.slice(-n).join(' '), country);
        if (region) {
            state = region.code;
            country = country ?? region.country;
            s = tidy(comma < 0 ? tail.slice(0, -n).join(' ') : `${s.slice(0, comma)}${tail.length > n ? `, ${tail.slice(0, -n).join(' ')}` : ''}`);
            break;
        }
    }
    const { street, city } = streetAndCity(s);
    if (!street)
        return null;
    return { street, city, state, zip: (country && normalizePostalCode(country, zip)) || zip, country: country ?? '' };
}
/**
 * The stored form of a site's region: the state or province as its code and the country as US or CA when either is
 * recognised (the country from the region or the postal code when it is missing), and the postal code in its stored
 * form. Anything unrecognised is kept as typed. Note the country 'CA' is Canada; California is only ever a state.
 */
export function normalizeSiteRegion(site) {
    const typedText = (site.country ?? '').trim(), typed = homeCountryFromAny(typedText);
    // A country typed but not recognised stays as typed: nothing is inferred over it.
    if (typedText && !typed)
        return site;
    const region = resolveHomeRegion((site.state ?? '').trim(), typed);
    const country = typed ?? region?.country ?? countryFromPostal(site.zip);
    const zip = country ? normalizePostalCode(country, site.zip) : null;
    return {
        ...site,
        ...(country ? { country } : {}),
        ...(region ? { state: region.code } : {}),
        ...(zip ? { zip } : {}),
    };
}
/** "Fresno, CA 93711, US": the confirmation line under the address field. */
export function siteAddressSummary(p) {
    return [p.city, [p.state, p.zip].filter(Boolean).join(' '), p.country].map(x => (x ?? '').trim()).filter(Boolean).join(', ');
}
//# sourceMappingURL=siteAddress.js.map